import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import './ActivityChartModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ActivityChartModal = ({ isOpen, onClose, data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  const [chartData, setChartData] = useState({
    activeUsers: 0,
    dailyData: [],
    loading: true,
    error: null
  });

  // Fetch real data from backend
  useEffect(() => {
    if (!isOpen) return;

    const fetchActivityData = async () => {
      try {
        setChartData(prev => ({ ...prev, loading: true, error: null }));
        
        const response = await fetch(`${API_BASE_URL}/dashboard/activity-chart`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch activity data');
        }

        const data = await response.json();
        
        // Calculate current active users from recent activity
        const recentActivity = data.daily?.totalActivities?.slice(-3) || [0, 0, 0];
        const activeUsers = recentActivity[recentActivity.length - 1] || 0;
        
        setChartData({
          activeUsers: activeUsers,
          dailyData: data.daily?.totalActivities || [],
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Error fetching activity data:', error);
        setChartData({
          activeUsers: 0,
          dailyData: [0, 0, 0, 0, 0, 0, 0],
          loading: false,
          error: error.message
        });
      }
    };

    // Initial fetch
    fetchActivityData();
    
    // Update every 30 seconds
    const interval = setInterval(fetchActivityData, 30000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && chartRef.current && !chartData.loading) {
      // Destroy existing chart
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      // Create simple line chart with real data
      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          datasets: [{
            label: 'Daily Activities',
            data: chartData.dailyData,
            borderColor: '#4285f4',
            backgroundColor: 'rgba(66, 133, 244, 0.1)',
            tension: 0.4,
            fill: true,
            borderWidth: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: '#f1f3f4'
              }
            },
            x: {
              grid: {
                display: false
              }
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [isOpen, chartData]);

  if (!isOpen) return null;

  return (
    <div className="activity-chart-modal-overlay">
      <div className="activity-chart-modal">
        <div className="modal-header">
          <h2>Real-Time Activity</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {chartData.loading ? (
            <div className="loading-state">
              <p>Loading activity data...</p>
            </div>
          ) : chartData.error ? (
            <div className="error-state">
              <p>Error: {chartData.error}</p>
            </div>
          ) : (
            <>
              <div className="metric-card">
                <h3>Active Users</h3>
                <p className="metric-value">{chartData.activeUsers}</p>
                <span className="metric-label">Currently online</span>
              </div>

              <div className="chart-section">
                <h3>Weekly Activity</h3>
                <div className="chart-wrapper">
                  <canvas ref={chartRef}></canvas>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="close-modal-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityChartModal; 